import { Controller } from "@hotwired/stimulus"
import { confirmAction } from "../lib/confirm"

// Phase 3b — admin: listings page three-dot menu actions that need
// client-side UI (the single-field mutations stay as button_to forms).
//
//   * API Credentials  → read-only modal of the listing's auth config
//   * Setup Info       → right-anchored slide-over (steps + doc links)
//   * Connected Companies → modal, lazy-fetched JSON of live connections
//   * Delete           → styled confirm (lib/confirm), then DELETE
//
// Credentials + setup data ride along in data attributes (already loaded
// server-side, no extra query). Connected companies is fetched on open.
export default class extends Controller {
  static targets = [
    "credModal", "credBody",
    "setupSlide", "setupPanel", "setupBody", "setupTitle",
    "companiesModal", "companiesBody", "companiesTitle"
  ]
  static values = {
    destroyUrlTemplate: String,
    connectedCompaniesUrlTemplate: String
  }

  connect() {
    this._showPendingFlash()
  }

  // ── API Credentials modal ─────────────────────────────
  openCredentials(event) {
    const data = this._parse(event.currentTarget.dataset.credentials)
    const name = event.currentTarget.dataset.name || "Listing"
    this.credBodyTarget.innerHTML = this._credHtml(name, data)
    this._openModal(this.credModalTarget)
  }
  closeCredentials() { this._closeModal(this.credModalTarget) }

  _credHtml(name, d) {
    const rows = [
      ["Auth kind", d.auth_kind],
      ["Client ID env", d.client_id_env],
      ["Client secret env", d.client_secret_env],
      ["Authorize URL", d.authorize_url],
      ["Token URL", d.token_url],
      ["Userinfo URL", d.userinfo_url]
    ].filter(([, v]) => v)
    let html = `<dl class="divide-y divide-gray-100">`
    rows.forEach(([label, value]) => {
      html += `<div class="flex items-start justify-between gap-4 py-2">
        <dt class="text-[13px] text-gray-500 shrink-0">${this._esc(label)}</dt>
        <dd class="text-[12px] font-mono text-gray-800 text-right break-all">${this._esc(value)}</dd>
      </div>`
    })
    html += `</dl>`
    const scopes = Array.isArray(d.scopes) ? d.scopes : []
    if (scopes.length) {
      html += `<div class="mt-3"><div class="text-[13px] font-medium text-gray-700 mb-1.5">Default scopes</div><div class="flex flex-wrap gap-1.5">`
      scopes.forEach(s => {
        const req = s.required ? " · required" : ""
        html += `<span class="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-mono bg-gray-100 text-gray-700" title="${this._esc(s.label || "")}${this._esc(req)}">${this._esc(s.slug)}</span>`
      })
      html += `</div></div>`
    }
    if (!rows.length && !scopes.length) {
      html = `<p class="text-[13px] text-gray-500">No credential configuration on this listing.</p>`
    }
    return html
  }

  // ── Setup Info slide-over ─────────────────────────────
  openSetup(event) {
    const data = this._parse(event.currentTarget.dataset.setup)
    const name = event.currentTarget.dataset.name || "Listing"
    this.setupTitleTarget.textContent = name
    this.setupBodyTarget.innerHTML = this._setupHtml(data)
    this.setupSlideTarget.classList.remove("hidden")
    document.dispatchEvent(new CustomEvent("modal:open"))
    // Slide the panel in on the next frame.
    requestAnimationFrame(() => this.setupPanelTarget.classList.remove("translate-x-full"))
  }
  closeSetup() {
    this.setupPanelTarget.classList.add("translate-x-full")
    setTimeout(() => this.setupSlideTarget.classList.add("hidden"), 200)
  }

  _setupHtml(d) {
    let html = ""
    const steps = Array.isArray(d.steps) ? d.steps : []
    if (steps.length) {
      html += `<div class="text-[13px] font-medium text-gray-700 mb-2">Setup steps</div><ol class="space-y-2 mb-5">`
      steps.forEach((step, i) => {
        html += `<li class="flex gap-2.5">
          <span class="shrink-0 w-5 h-5 rounded-full bg-brand-50 text-brand text-[11px] font-semibold flex items-center justify-center">${i + 1}</span>
          <span class="text-[13px] text-gray-700">${this._esc(step)}</span>
        </li>`
      })
      html += `</ol>`
    } else {
      html += `<p class="text-[13px] text-gray-500 mb-5">No setup steps documented yet.</p>`
    }
    if (d.data_flow_summary) {
      html += `<div class="text-[13px] font-medium text-gray-700 mb-1">Data flow</div>
        <p class="text-[13px] text-gray-600 mb-5">${this._esc(d.data_flow_summary)}</p>`
    }
    const links = [
      ["Documentation", d.docs_url],
      ["Support", d.support_url],
      ["Privacy policy", d.privacy_policy_url],
      ["Security docs", d.security_doc_url]
    ].filter(([, v]) => v)
    if (links.length) {
      html += `<div class="text-[13px] font-medium text-gray-700 mb-2">Links</div><div class="space-y-1.5">`
      links.forEach(([label, url]) => {
        html += `<a href="${this._esc(url)}" target="_blank" rel="noopener"
          class="flex items-center gap-2 text-[13px] text-brand hover:underline">
          <span>${this._esc(label)}</span>
        </a>`
      })
      html += `</div>`
    }
    return html
  }

  // ── Connected Companies modal ─────────────────────────
  async openCompanies(event) {
    const id = event.currentTarget.dataset.id
    const name = event.currentTarget.dataset.name || "Listing"
    this.companiesTitleTarget.textContent = `Companies connected to ${name}`
    this.companiesBodyTarget.innerHTML = `<p class="text-[13px] text-gray-500 py-6 text-center">Loading…</p>`
    this._openModal(this.companiesModalTarget)
    const url = this.connectedCompaniesUrlTemplateValue.replace("ID", id)
    try {
      const res = await fetch(url, {
        credentials: "same-origin",
        headers: { "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        this.companiesBodyTarget.innerHTML = this._companiesHtml(data.companies || [])
      } else {
        this.companiesBodyTarget.innerHTML = `<p class="text-[13px] text-rose-600 py-6 text-center">${this._esc(data.message || "Couldn't load connections.")}</p>`
      }
    } catch (_) {
      this.companiesBodyTarget.innerHTML = `<p class="text-[13px] text-rose-600 py-6 text-center">Network error — try again.</p>`
    }
  }
  closeCompanies() { this._closeModal(this.companiesModalTarget) }

  _companiesHtml(companies) {
    if (!companies.length) {
      return `<p class="text-[13px] text-gray-500 py-6 text-center">No companies are connected to this listing yet.</p>`
    }
    const pill = (status) => {
      const map = {
        active: "bg-emerald-100 text-emerald-700",
        failed: "bg-rose-100 text-rose-700",
        disabled: "bg-gray-100 text-gray-600",
        awaiting_reauth: "bg-amber-100 text-amber-700",
        pending: "bg-blue-100 text-blue-700"
      }
      const cls = map[status] || "bg-gray-100 text-gray-600"
      return `<span class="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${cls}">${this._esc(status)}</span>`
    }
    let html = `<ul class="divide-y divide-gray-100">`
    companies.forEach(c => {
      html += `<li class="flex items-center justify-between gap-3 py-2">
        <div class="min-w-0">
          <div class="text-[13px] font-medium text-gray-900 truncate">${this._esc(c.name)}</div>
          ${c.account ? `<div class="text-[11px] text-gray-500 truncate">${this._esc(c.account)}</div>` : ""}
        </div>
        <div class="flex items-center gap-2 shrink-0">
          ${c.last_synced_at ? `<span class="text-[11px] text-gray-400">${this._esc(c.last_synced_at)}</span>` : ""}
          ${pill(c.status)}
        </div>
      </li>`
    })
    html += `</ul>`
    return html
  }

  // ── Delete (styled confirm) ───────────────────────────
  async confirmDelete(event) {
    const id = event.currentTarget.dataset.id
    const name = event.currentTarget.dataset.name || "this listing"
    const connected = event.currentTarget.dataset.connected === "true"
    if (connected) {
      await confirmAction({
        title: "Can't delete listing",
        message: `${name} has companies connected to it. Disconnect them before deleting.`,
        confirmLabel: "OK",
        cancelLabel: "Close"
      })
      return
    }
    const ok = await confirmAction({
      title: `Delete ${name}?`,
      message: "This permanently removes the listing from the marketplace. This can't be undone.",
      confirmLabel: "Delete",
      danger: true
    })
    if (!ok) return

    const url = this.destroyUrlTemplateValue.replace("ID", id)
    try {
      const res = await fetch(url, {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) this._flashAndReload(data.message || "Listing deleted.", "notice")
      else this._toast(data.message || "Couldn't delete listing.", "alert")
    } catch (_) {
      this._toast("Network error — try again.", "alert")
    }
  }

  // ── Modal plumbing ────────────────────────────────────
  _openModal(modal) {
    modal.classList.remove("hidden")
    document.dispatchEvent(new CustomEvent("modal:open"))
  }
  _closeModal(modal) { modal.classList.add("hidden") }
  stopPropagation(event) { event.stopPropagation() }

  // ── Helpers ───────────────────────────────────────────
  _parse(raw) {
    try { return JSON.parse(raw || "{}") } catch (_) { return {} }
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _esc(str) {
    return String(str ?? "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }

  _toast(message, type) {
    const div = document.createElement("div")
    div.className = type === "notice" ? "flash-toast-notice" : "flash-toast-alert"
    div.dataset.controller = "flash"
    div.dataset.flashDismissAfterValue = "5000"
    div.textContent = message
    document.body.prepend(div)
  }

  _flashAndReload(message, type) {
    try {
      sessionStorage.setItem("integration-admin.flash",
        JSON.stringify({ message, type, ts: Date.now() }))
    } catch (_) {}
    window.location.reload()
  }

  _showPendingFlash() {
    let raw
    try { raw = sessionStorage.getItem("integration-admin.flash") } catch (_) { return }
    if (!raw) return
    try { sessionStorage.removeItem("integration-admin.flash") } catch (_) {}
    try {
      const { message, type } = JSON.parse(raw)
      if (message) this._toast(message, type === "alert" ? "alert" : "notice")
    } catch (_) {}
  }
}
